import { useCallback, useEffect, useRef, useState } from "react";

export enum StreamingMessageType {
  CLOSE = "CLOSE",
  ERROR = "ERROR",
  RESPONSE = "RESPONSE",
}

type StreamingMessage = {
  type: StreamingMessageType;
  payload: string | StreamingResponsePayload;
};

enum StreamingResponsePayloadType {
  TOKEN = "TOKEN",
  INFO = "INFO",
}

type StreamingResponsePayload = {
  type: StreamingResponsePayloadType;
  payload: string | StreamingInfoPayload;
};

enum StreamingInfoPayloadType {
  INCOMPLETE_RESULTS = "INCOMPLETE_RESULTS",
  GLOBAL_DAILY_COST_LIMIT = "GLOBAL_DAILY_COST_LIMIT",
  DISPUTED_RESULTS = "DISPUTED_RESULTS",
  RESULTS_ANALYZED_COUNT = "RESULTS_ANALYZED_COUNT",
  NUANCE_REQUIRED = "NUANCE_REQUIRED",
  FULL_TEXT_PAPER_IDS = "FULL_TEXT_PAPER_IDS",
}

type StreamingInfoPayload = {
  type: StreamingInfoPayloadType;
  payload?: number | string[];
};

type UseStreamingProps = {
  enabled: boolean;
  onSuccess: () => void;
  onError?: (error: string, isAuthError: boolean) => void;
  queryKey?: string;
  getEventSource: () => EventSource | undefined;
};

const useStreaming = ({
  queryKey,
  getEventSource,
  onSuccess,
  onError,
  enabled,
}: UseStreamingProps) => {
  const [isSuccess, setIsSuccess] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState<string>();
  const [isAuthError, setIsAuthError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [content, setContent] = useState<string>("");
  const [resultCount, setResultCount] = useState<number>();
  const [isIncompleteResults, setIsIncompleteResults] = useState(false);
  const [isDailyLimitReached, setIsDailyLimitReached] = useState(false);
  const [isDisputed, setIsDisputed] = useState(false);
  const [isNuanceRequired, setIsNuanceRequired] = useState(false);
  const [fullTextPaperIds, setFullTextPaperIds] = useState<string[]>([]);

  const currentQueryKeyRef = useRef<string>();
  const eventSourceRef = useRef<EventSource | null>(null);
  const onSuccessCalledRef = useRef<boolean>(false);

  const handleInfo = useCallback((info: StreamingInfoPayload) => {
    switch (info.type) {
      case StreamingInfoPayloadType.INCOMPLETE_RESULTS:
        setIsIncompleteResults(true);
        break;
      case StreamingInfoPayloadType.GLOBAL_DAILY_COST_LIMIT:
        setIsDailyLimitReached(true);
        break;
      case StreamingInfoPayloadType.DISPUTED_RESULTS:
        setIsDisputed(true);
        break;
      case StreamingInfoPayloadType.RESULTS_ANALYZED_COUNT:
        setResultCount(info.payload as number);
        break;
      case StreamingInfoPayloadType.NUANCE_REQUIRED:
        setIsNuanceRequired(true);
        break;
      case StreamingInfoPayloadType.FULL_TEXT_PAPER_IDS:
        setFullTextPaperIds(info.payload as string[]);
        break;
    }
  }, []);

  const handleResponse = useCallback(
    (response: StreamingResponsePayload) => {
      switch (response.type) {
        case StreamingResponsePayloadType.TOKEN:
          // isLoading represents that the backend is processing the request. Turn it off as soon as tokens start to arrive.
          setIsLoading(false);
          setIsStreaming(true);
          setContent((prevContent) => prevContent + response.payload);
          break;
        case StreamingResponsePayloadType.INFO:
          handleInfo(response.payload as StreamingInfoPayload);
      }
    },
    [handleInfo]
  );

  const handleMessageRef = useRef((message: StreamingMessage) => {});

  useEffect(() => {
    handleMessageRef.current = (message: StreamingMessage) => {
      switch (message.type) {
        case StreamingMessageType.RESPONSE:
          handleResponse(message.payload as StreamingResponsePayload);
          if (!onSuccessCalledRef.current) {
            onSuccess();
            onSuccessCalledRef.current = true;
          }
          setIsSuccess(true);
          break;
        case StreamingMessageType.ERROR:
          setIsStreaming(false); // Set streaming to false on error
          const errorMessage = message.payload as string;
          const isAuth = errorMessage === "Not Authenticated";
          if (isAuth) {
            setIsAuthError(true);
          } else {
            console.error("Streaming Error:", errorMessage);
            setIsError(true);
          }
          setIsError(true);
          setError(errorMessage);
          setIsLoading(false);
          if (onError) {
            onError(errorMessage, isAuth);
          }
          eventSourceRef.current?.close();
          break;
        case StreamingMessageType.CLOSE:
          setIsLoading(false);
          setIsComplete(true);
          setIsStreaming(false); // Set streaming to false when closed
          eventSourceRef.current?.close();
          break;
      }
    };
  }, [handleResponse, onSuccess, onError]);

  const handleMessage = (message: StreamingMessage) => {
    handleMessageRef.current(message);
  };

  const resetWithoutContent = () => {
    setIsSuccess(false);
    setIsComplete(false);
    setIsDisputed(false);
    setIsError(false);
    setError("");
    setIsAuthError(false);
    setIsDailyLimitReached(false);
    setIsIncompleteResults(false);
    setIsLoading(false);
    setIsStreaming(false);
    setResultCount(undefined);
    setIsNuanceRequired(false);
    setFullTextPaperIds([]);
    onSuccessCalledRef.current = false;
  };

  const reset = useCallback(() => {
    setContent("");
    resetWithoutContent();
  }, []);

  useEffect(() => {
    if (!enabled) {
      // If stream was successful and complete, preserve content
      if (isSuccess && isComplete) {
        resetWithoutContent();
      } else {
        // If stream was not successful or incomplete, reset everything including content
        reset();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  useEffect(() => {
    if (!enabled || queryKey === undefined) {
      return;
    }

    if (currentQueryKeyRef.current === queryKey) return;

    currentQueryKeyRef.current = queryKey;

    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    reset();
    setIsLoading(true);
    setIsStreaming(true);

    const eventSource = getEventSource();

    if (eventSource === undefined) return;

    eventSourceRef.current = eventSource;

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        handleMessage(data as StreamingMessage);
      } catch (e) {} // If not valid JSON, (e.g. for a 'ping'), skip the message
    };

    eventSource.onerror = (error) => {
      console.error("Streaming Error:", error);
      setIsError(true);
      setError("Streaming Error");
      setIsLoading(false);
      setIsStreaming(false);
      if (onError) {
        onError("Streaming Error", false);
      }
      eventSource.close();
    };

    return () => {
      // Close stream on unmount.
      if (!enabled) {
        eventSource.close();
        setIsStreaming(false);
      }
    };
  }, [queryKey, enabled, getEventSource, onError, reset]);

  return {
    content,
    error,
    fullTextPaperIds,
    isAuthError,
    isComplete,
    isDailyLimitReached,
    isDisputed,
    isError,
    isIncompleteResults,
    isLoading,
    isStreaming,
    isSuccess,
    isNuanceRequired,
    resultCount,
  };
};

export default useStreaming;
